/**
 * POS-specific API service for employees, customers, transactions, and inventory management
 * This provides typed methods for all POS-related backend endpoints
 */
import { apiService } from './api'
import type { Product, Transaction, Customer, Supplier, GRN, StockBatch } from '@/types/pos'

// ============================================================================
// Response Types
// ============================================================================

interface ApiResponse<T> {
    success: boolean
    data: T
    message?: string
}

// ============================================================================
// Employee Types
// ============================================================================


export interface Employee {
    id: string
    _id?: string
    name: string
    email: string
    phone?: string
    role: 'admin' | 'manager' | 'cashier' | 'staff'
    isActive: boolean
    lastActive?: string
    createdAt: string
    updatedAt: string
}

export interface CreateEmployeeData {
    name: string
    email: string
    password: string
    role?: 'admin' | 'manager' | 'cashier' | 'staff'
    phone?: string
    isActive?: boolean
}

export interface UpdateEmployeeData {
    name?: string
    email?: string
    phone?: string
    role?: 'admin' | 'manager' | 'cashier' | 'staff'
    isActive?: boolean
}

// ============================================================================
// Customer Types
// ============================================================================

export interface CreateCustomerData {
    name: string
    phone: string
    email?: string
    address?: string
    notes?: string
}

export interface UpdateCustomerData {
    name?: string
    phone?: string
    email?: string
    address?: string
    notes?: string
}

// ============================================================================
// Stock Update Types
// ============================================================================

export type StockOperation = 'add' | 'subtract' | 'set'

export interface StockUpdateData {
    quantity: number
    operation: StockOperation
}

export interface StockUpdateResponse {
    success: boolean
    data: Product
    previousStock: number
    newStock: number
}

// ============================================================================
// Transaction Types
// ============================================================================

export interface RefundData {
    items?: Array<{
        productId: string
        quantity: number
        refundAmount: number
    }>
    reason: string
    refundAmount?: number
}

// ============================================================================
// Report Types
// ============================================================================

export interface DashboardStats {
    kpis: {
        totalRevenue: number
        todayRevenue: number
        totalTransactions: number
        todayTransactions: number
        totalCustomers: number
        totalProducts: number
        avgOrderValue: number
        lowStockItems: number
        outOfStockItems: number
    }
    charts: {
        revenue: Array<{ period: string; value: number }>
        expenses: Array<{ period: string; value: number }>
        sales: Array<{ name: string; value: number }>
    }
    recentActivities: Array<{
        id: string
        type: string
        description: string
        amount: number
        createdAt: string
    }>
}

export interface ReportData {
    summary: {
        revenue: number
        transactions: number
        products: number
    }
    trajectory: Array<{ period: string; value: number; label: string }>
    topProducts: Array<{
        productId: string
        productName: string
        quantity: number
        revenue: number
    }>
    salesByCategory: Array<{
        category: string
        sales: number
        percentage: number
    }>
    recentTransactions: Array<{
        id: string
        amount: number
        customer: string
        createdAt: string
    }>
}

// ============================================================================
// POS API Service Class
// ============================================================================

class POSApiService {
    // ==========================================================================
    // Employee Endpoints
    // ==========================================================================

    /**
     * Get all employees (managers, cashiers, staff)
     */
    async getEmployees(): Promise<ApiResponse<Employee[]>> {
        return apiService.get<ApiResponse<Employee[]>>('/employees')
    }

    /**
     * Get a single employee by ID
     */
    async getEmployee(id: string): Promise<ApiResponse<Employee>> {
        return apiService.get<ApiResponse<Employee>>(`/employees/${id}`)
    }

    /**
     * Create a new employee (Admin only)
     */
    async createEmployee(data: CreateEmployeeData): Promise<ApiResponse<Employee>> {
        return apiService.post<ApiResponse<Employee>>('/employees', data)
    }

    /**
     * Update an existing employee (Admin only)
     */
    async updateEmployee(id: string, data: UpdateEmployeeData): Promise<ApiResponse<Employee>> {
        return apiService.put<ApiResponse<Employee>>(`/employees/${id}`, data)
    }

    /**
     * Delete an employee (Admin only)
     */
    async deleteEmployee(id: string): Promise<ApiResponse<null>> {
        return apiService.delete<ApiResponse<null>>(`/employees/${id}`)
    }

    // ==========================================================================
    // Customer Endpoints
    // ==========================================================================

    /**
     * Get all customers
     */
    async getCustomers(): Promise<ApiResponse<Customer[]>> {
        return apiService.get<ApiResponse<Customer[]>>('/customers')
    }

    /**
     * Search customers by name, email, or phone
     */
    async searchCustomers(query: string): Promise<ApiResponse<Customer[]>> {
        return apiService.get<ApiResponse<Customer[]>>(`/customers/search?q=${encodeURIComponent(query)}`)
    }

    /**
     * Get a single customer by ID
     */
    async getCustomer(id: string): Promise<ApiResponse<Customer>> {
        return apiService.get<ApiResponse<Customer>>(`/customers/${id}`)
    }

    /**
     * Create a new customer
     */
    async createCustomer(data: CreateCustomerData): Promise<ApiResponse<Customer>> {
        return apiService.post<ApiResponse<Customer>>('/customers', data)
    }

    /**
     * Update an existing customer
     */
    async updateCustomer(id: string, data: UpdateCustomerData): Promise<ApiResponse<Customer>> {
        return apiService.put<ApiResponse<Customer>>(`/customers/${id}`, data)
    }

    /**
     * Delete a customer
     */
    async deleteCustomer(id: string): Promise<ApiResponse<null>> {
        return apiService.delete<ApiResponse<null>>(`/customers/${id}`)
    }

    // ==========================================================================
    // Supplier Endpoints
    // ==========================================================================

    /**
     * Get all suppliers
     */
    async getSuppliers(): Promise<ApiResponse<Supplier[]>> {
        return apiService.get<ApiResponse<Supplier[]>>('/suppliers')
    }

    /**
     * Search suppliers by name, company, email, or phone
     */
    async searchSuppliers(query: string): Promise<ApiResponse<Supplier[]>> {
        return apiService.get<ApiResponse<Supplier[]>>(`/suppliers/search?q=${encodeURIComponent(query)}`)
    }

    /**
     * Get a single supplier by ID
     */
    async getSupplier(id: string): Promise<ApiResponse<Supplier>> {
        return apiService.get<ApiResponse<Supplier>>(`/suppliers/${id}`)
    }

    /**
     * Create a new supplier
     */
    async createSupplier(data: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
        return apiService.post<ApiResponse<Supplier>>('/suppliers', data)
    }

    /**
     * Update an existing supplier
     */
    async updateSupplier(id: string, data: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
        return apiService.put<ApiResponse<Supplier>>(`/suppliers/${id}`, data)
    }

    /**
     * Delete a supplier
     */
    async deleteSupplier(id: string): Promise<ApiResponse<null>> {
        return apiService.delete<ApiResponse<null>>(`/suppliers/${id}`)
    }

    // ==========================================================================
    // GRN Endpoints
    // ==========================================================================

    /**
     * Get all GRNs
     */
    async getGRNs(): Promise<ApiResponse<GRN[]>> {
        return apiService.get<ApiResponse<GRN[]>>('/grn')
    }

    /**
     * Get a single GRN by ID
     */
    async getGRN(id: string): Promise<ApiResponse<GRN>> {
        return apiService.get<ApiResponse<GRN>>(`/grn/${id}`)
    }

    /**
     * Create a new GRN (receives goods and updates stock)
     */
    async createGRN(data: Partial<GRN>): Promise<ApiResponse<GRN>> {
        return apiService.post<ApiResponse<GRN>>('/grn', data)
    }

    /**
     * Delete a GRN
     */
    async deleteGRN(id: string): Promise<ApiResponse<null>> {
        return apiService.delete<ApiResponse<null>>(`/grn/${id}`)
    }

    // ==========================================================================
    // Stock Batch Endpoints
    // ==========================================================================

    /**
     * Get all batches (optionally filtered by productId)
     */
    async getBatches(productId?: string): Promise<ApiResponse<StockBatch[]>> {
        const url = productId ? `/batches?productId=${encodeURIComponent(productId)}` : '/batches'
        return apiService.get<ApiResponse<StockBatch[]>>(url)
    }

    /**
     * Get active batches for a specific product (used by POS batch selection)
     */
    async getActiveBatchesForProduct(productId: string): Promise<ApiResponse<StockBatch[]>> {
        return apiService.get<ApiResponse<StockBatch[]>>(`/batches/product/${encodeURIComponent(productId)}`)
    }

    /**
     * Create a manual batch
     */
    async createBatch(data: Partial<StockBatch>): Promise<ApiResponse<StockBatch>> {
        return apiService.post<ApiResponse<StockBatch>>('/batches', data)
    }

    /**
     * Update a batch
     */
    async updateBatch(id: string, data: Partial<StockBatch>): Promise<ApiResponse<StockBatch>> {
        return apiService.put<ApiResponse<StockBatch>>(`/batches/${id}`, data)
    }

    /**
     * Delete a batch
     */
    async deleteBatch(id: string): Promise<ApiResponse<null>> {
        return apiService.delete<ApiResponse<null>>(`/batches/${id}`)
    }

    // ==========================================================================
    // Product & Inventory Endpoints
    // ==========================================================================

    /**
     * Get products with low stock levels
     */
    async getLowStockProducts(): Promise<ApiResponse<Product[]>> {
        return apiService.get<ApiResponse<Product[]>>('/products/low-stock')
    }

    /**
     * Update product stock level
     * @param id - Product ID
     * @param data - Stock update operation and quantity
     */
    async updateStock(id: string, data: StockUpdateData): Promise<StockUpdateResponse> {
        return apiService.patch<StockUpdateResponse>(`/products/${id}/stock`, data)
    }

    /**
     * Look up product by barcode, ISBN, or SKU
     */
    async getProductByBarcode(barcode: string): Promise<ApiResponse<Product>> {
        return apiService.get<ApiResponse<Product>>(`/products/barcode/${encodeURIComponent(barcode)}`)
    }

    // ==========================================================================
    // Transaction Endpoints
    // ==========================================================================

    /**
     * Get all transactions
     */
    async getTransactions(): Promise<ApiResponse<Transaction[]>> {
        return apiService.get<ApiResponse<Transaction[]>>('/transactions')
    }

    /**
     * Get a single transaction by ID
     */
    async getTransaction(id: string): Promise<ApiResponse<Transaction>> {
        return apiService.get<ApiResponse<Transaction>>(`/transactions/${id}`)
    }

    /**
     * Process a refund for a transaction
     */
    async processRefund(transactionId: string, data: RefundData): Promise<ApiResponse<Transaction>> {
        return apiService.post<ApiResponse<Transaction>>(`/transactions/${transactionId}/refund`, data)
    }

    /**
     * Void a transaction (same day only). Optionally send reason for audit.
     */
    async voidTransaction(id: string, reason?: string): Promise<ApiResponse<null>> {
        const config = reason != null && reason.trim() ? { data: { reason: reason.trim() } } : undefined
        return apiService.delete<ApiResponse<null>>(`/transactions/${id}`, config)
    }

    // ==========================================================================
    // Statistics & Reports Endpoints
    // ==========================================================================

    /**
     * Get dashboard statistics (KPIs, charts, recent activities)
     */
    async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
        return apiService.get<ApiResponse<DashboardStats>>('/stats/dashboard')
    }

    /**
     * Get reports for a specific time period or date range
     */
    async getReports(params: { period?: '1d' | '7d' | '30d'; from?: string; to?: string } = {}): Promise<ApiResponse<ReportData>> {
        const q = new URLSearchParams()
        if (params.period) q.set('period', params.period)
        if (params.from) q.set('from', params.from)
        if (params.to) q.set('to', params.to)
        const query = q.toString()
        return apiService.get<ApiResponse<ReportData>>(`/stats/reports${query ? '?' + query : ''}`)
    }

    /** Item-wise sales report (by period) */
    async getItemSalesReport(params: { from?: string; to?: string; category?: string }): Promise<ApiResponse<ItemSalesRow[]>> {
        const q = new URLSearchParams()
        if (params.from) q.set('from', params.from)
        if (params.to) q.set('to', params.to)
        if (params.category) q.set('category', params.category)
        return apiService.get<ApiResponse<ItemSalesRow[]>>(`/stats/reports/item-sales?${q.toString()}`)
    }

    /** Cashier-wise sales report */
    async getCashierSalesReport(params: { from?: string; to?: string; cashierId?: string }): Promise<ApiResponse<CashierSalesRow[]>> {
        const q = new URLSearchParams()
        if (params.from) q.set('from', params.from)
        if (params.to) q.set('to', params.to)
        if (params.cashierId) q.set('cashierId', params.cashierId)
        return apiService.get<ApiResponse<CashierSalesRow[]>>(`/stats/reports/cashier-sales?${q.toString()}`)
    }

    /** Income breakdown by payment method and period */
    async getIncomeReport(params: { from?: string; to?: string; groupBy?: 'day' | 'week' | 'month' }): Promise<ApiResponse<IncomeReportData>> {
        const q = new URLSearchParams()
        if (params.from) q.set('from', params.from)
        if (params.to) q.set('to', params.to)
        if (params.groupBy) q.set('groupBy', params.groupBy)
        return apiService.get<ApiResponse<IncomeReportData>>(`/stats/reports/income?${q.toString()}`)
    }

    /** Products below reorder level */
    async getReorderReport(): Promise<ApiResponse<ReorderRow[]>> {
        return apiService.get<ApiResponse<ReorderRow[]>>('/stats/reports/reorder')
    }

    /** Expired / expiring items */
    async getExpiryReport(filter: 'expired' | 'today' | 'month' | '14days' = 'expired'): Promise<ApiResponse<ExpiryRow[]>> {
        return apiService.get<ApiResponse<ExpiryRow[]>>(`/stats/reports/expiry?filter=${filter}`)
    }

    /** End-of-day summary for a given date */
    async getDaySummary(date?: string): Promise<ApiResponse<DaySummaryData>> {
        const q = date ? `?date=${encodeURIComponent(date)}` : ''
        return apiService.get<ApiResponse<DaySummaryData>>(`/stats/day-summary${q}`)
    }
}

// Phase 3 report row types (backend response shapes)
export interface ItemSalesRow {
    productId: string
    productName: string
    sku: string
    quantity: number
    revenue: number
    avgPrice: number
}
export interface CashierSalesRow {
    cashierId: string
    cashierName: string
    totalInvoices: number
    totalRevenue: number
    avgTransaction: number
}
export interface IncomeReportData {
    byPaymentMethod: Array<{ method: string; amount: number }>
    byPeriod: Array<{ period: string; amount: number }>
    totalIncome: number
}
export interface ReorderRow {
    productId: string
    productName: string
    sku: string
    currentStock: number
    reorderLevel: number
    suggestedReorderQty: number
    supplierId?: string
    supplierName?: string
}
export interface ExpiryRow {
    id: string
    name?: string
    title?: string
    expiryDate: string
    isExpired: boolean
    daysUntilExpiry: number
    stock?: number
}
export interface DaySummaryData {
    date: string
    totalSales: number
    totalTransactions: number
    totalRefunds: number
    cashInHand: number
    cardPayments: number
    cashierBreakdown: Array<{ cashierId: string; cashierName: string; totalInvoices: number; totalRevenue: number }>
    topSellingItems: Array<{ productId: string; name: string; quantity: number }>
}

// Export singleton instance
export const posApiService = new POSApiService()
export default posApiService
